import { useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useRosterFunctions } from 'hooks';
import { omit } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useRecoilValue } from 'recoil';

import {
  PlanUnitType,
  ScenarioAdditionalCapacityType,
  Units
} from 'components/enums';

import { getMaxDate } from 'utils/date';

import { getPlanUnits } from 'RestAPIs/cost-centers';

import { scenarioAtom } from '../../../atoms';

import { Button, DialogActions, TextField } from '@mui/material';

import {
  DatePicker,
  LoadingOverlay,
  Select,
  VirtualizedAutocomplete
} from 'components/shared';
import {
  useGetOptionLabel,
  useRenderOption
} from 'components/shared/PlanUnitSelector/PlanUnitSelector.hooks';

import { StyledDialogMedium } from 'components/shared/Dialog/Dialog.styles';
import {
  GridCompact4,
  GridCompact6,
  GridCompact8,
  GridContainerFlex
} from 'components/shared/Grid/Grid.components';
import { formatAdditionalCapacityDataForSave } from '../../util';
import getAddEditAdditionalCapacitySchema from '../AdditionalCapacitySchema';
import { useSaveAdditionalCapacityQuery } from './queries';

import type {
  AdditionalCapacity,
  AdditionalCapacityWithoutId,
  PlanUnitMix
} from 'components/types';
import type { SubmitHandler } from 'react-hook-form';
import type { AdditionalCapacityForm } from './types';

const defaultFormValues = {
  description: '',
  quantity: 1,
  hours: 0,
  unit: '',
  startDate: new Date(new Date().getFullYear(), 0, 1),
  endDate: getMaxDate(),
  function: {},
  type: '',
  organizationalUnitId: {}
};

type Props = {
  isOpen: boolean;
  capacity?:
    | AdditionalCapacity
    | AdditionalCapacityWithoutId
    | Omit<AdditionalCapacityWithoutId, 'function'>
    | null;
  handleClose: () => void;
  onSave: () => void;
};

const getDefaultFormValues = (capacity) => {
  if (!capacity) {
    return defaultFormValues;
  }

  if ('function' in capacity) {
    return {
      ...omit(capacity, ['organizationalUnit']),
      startDate: new Date(capacity.startDate),
      endDate: new Date(capacity.endDate),
      organizationalUnitId: capacity.organizationalUnit
    };
  }

  return {
    ...omit(capacity, ['organizationalUnit']),
    startDate: new Date(capacity.startDate),
    endDate: new Date(capacity.endDate),
    organizationalUnitId: capacity.organizationalUnit
  };
};

const AddEditAdditionalCapacityDialog = ({
  isOpen,
  capacity,
  handleClose,
  onSave
}: Props) => {
  const scenario = useRecoilValue(scenarioAtom);
  const isDefault = scenario?.isDefault ?? false;
  const { t } = useTranslation('common');
  const {
    rosterFunctions: rosterFunctionOptions,
    isLoading: isLoadingRosterFunctions,
    isOptionDisabled: isRosterFunctionDisabled,
    renderOption: renderRosterFunctionOption,
    getOptionLabel: getRosterFunctionLabel
  } = useRosterFunctions();
  const [planUnitOptions, setPlanUnitOptions] = useState<PlanUnitMix[]>([]);
  const [isPlanUnitsLoading, setIsPlanUnitsLoading] = useState<boolean>(false);
  const getPlanUnitOptionLabel = useGetOptionLabel();

  const { control, handleSubmit, watch, reset } =
    useForm<AdditionalCapacityForm>({
      defaultValues: getDefaultFormValues(capacity),
      resolver: yupResolver(getAddEditAdditionalCapacitySchema())
    });

  const { mutate: saveAdditionalCapacity, isLoading } =
    useSaveAdditionalCapacityQuery({
      onSuccess: onSave
    });

  useEffect(() => {
    if (capacity) {
      reset(getDefaultFormValues(capacity));
    } else {
      reset(defaultFormValues);
    }
  }, [capacity]);

  const [watchType, watchStartDate, watchEndDate] = watch([
    'type',
    'startDate',
    'endDate'
  ]);

  const renderPlanUnitOption = useRenderOption({
    isMulti: false
  });

  const getPlanUnitOptions = async () => {
    setIsPlanUnitsLoading(true);

    const { data } = await getPlanUnits({
      startDate: watchStartDate,
      endDate: watchEndDate,
      type: 'public'
    });
    const planUnitType = getPlanUnitType();
    if (planUnitType === PlanUnitType.FLEX) {
      setPlanUnitOptions(
        data.filter(
          (d) =>
            d.type === PlanUnitType.COST_CENTER &&
            d.category === PlanUnitType.FLEX
        )
      );
    } else if (planUnitType === PlanUnitType.COST_CENTER) {
      setPlanUnitOptions(
        data.filter(
          (d) => d.type === PlanUnitType.COST_CENTER && d.category !== 'flex'
        )
      );
    } else {
      setPlanUnitOptions(data);
    }
    setIsPlanUnitsLoading(false);
  };

  useEffect(() => {
    getPlanUnitOptions();
  }, [watchType, watchStartDate, watchEndDate]);

  const onSubmit: SubmitHandler<AdditionalCapacityForm> = (data) => {
    if (!scenario) {
      return;
    }

    const payload = formatAdditionalCapacityDataForSave(data);

    saveAdditionalCapacity({
      payload,
      scenarioId: scenario.scenarioId,
      capacityId: capacity && 'id' in capacity ? capacity?.id : undefined
    });
  };

  const unitOptions = useMemo(
    () =>
      Object.keys(Units).map((unit) => ({
        value: unit,
        label: t(`timeUnits.${unit}`)
      })),
    []
  );

  const typeOptions = useMemo(
    () =>
      Object.keys(ScenarioAdditionalCapacityType)
        .filter((type) => (!isDefault || type !== 'BORROW') && type !== 'FLEX')
        .map((type) => ({
          value: type,
          label: t(`additionalCapacityTypes.${type}`)
        })),
    [isDefault]
  );

  const getPlanUnitType = () => {
    if (watchType === ScenarioAdditionalCapacityType.FLEX) {
      return PlanUnitType.FLEX;
    }

    if (scenario?.isDefault) {
      return PlanUnitType.COST_CENTER;
    }

    return null;
  };

  return (
    <StyledDialogMedium
      open={isOpen}
      title={
        capacity && 'id' in capacity
          ? t('capacity.editCapacity')
          : t('capacity.addCapacity')
      }
      handleClose={handleClose}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <GridContainerFlex>
          <GridCompact6>
            <Controller
              name="function"
              control={control}
              render={({ field, fieldState }) => (
                <VirtualizedAutocomplete
                  {...field}
                  onChange={(e, v) => {
                    field.onChange(v);
                  }}
                  optionLabelProp="description"
                  getOptionLabel={getRosterFunctionLabel}
                  getOptionDisabled={isRosterFunctionDisabled}
                  options={rosterFunctionOptions}
                  value={
                    capacity && 'function' in capacity
                      ? rosterFunctionOptions.find(
                          ({ id }) => id === capacity?.function?.id
                        )
                      : undefined
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      ref={params.InputProps.ref}
                      label={t('function')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? null}
                    />
                  )}
                  renderOption={renderRosterFunctionOption}
                  isOptionEqualToValue={(option, value) => {
                    return option?.id === value?.id;
                  }}
                  autoHighlight
                  loading={isLoadingRosterFunctions}
                />
              )}
            />
          </GridCompact6>
          <GridCompact6>
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  fullWidth
                  {...field}
                  label={t('description')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  required
                />
              )}
            />
          </GridCompact6>
        </GridContainerFlex>
        <GridContainerFlex>
          <GridCompact4>
            <Controller
              name="type"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  fullWidth
                  onSelected={field.onChange}
                  label={t('type')}
                  options={typeOptions}
                  error={fieldState.error}
                  valueProp={field.value}
                  required
                />
              )}
            />
          </GridCompact4>
          <GridCompact8>
            <Controller
              name="organizationalUnitId"
              control={control}
              render={({ field, fieldState }) => (
                <VirtualizedAutocomplete
                  {...field}
                  onChange={(e, v) => {
                    if (!v) {
                      field.onChange(null);
                    } else {
                      field.onChange(v);
                    }
                  }}
                  optionLabelProp="name"
                  getOptionLabel={(o) => getPlanUnitOptionLabel(o) ?? ''}
                  options={planUnitOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      ref={params.InputProps.ref}
                      label={`${
                        watchType === ScenarioAdditionalCapacityType.LOAN
                          ? t('to')
                          : t('from')
                      } ${t('planUnit').toLowerCase()}`}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? null}
                      required
                    />
                  )}
                  renderOption={renderPlanUnitOption}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  autoHighlight
                  loading={isPlanUnitsLoading}
                  disabled={!watchType}
                />
              )}
            />
          </GridCompact8>
        </GridContainerFlex>
        <GridContainerFlex>
          <GridCompact4>
            <Controller
              name="quantity"
              control={control}
              render={({ field, fieldState }) => (
                <NumericFormat
                  {...field}
                  customInput={TextField}
                  type="text"
                  onValueChange={({ value: v }) =>
                    field.onChange({ target: { value: v } })
                  }
                  label={t('quantity')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  decimalSeparator=","
                  valueIsNumericString
                  required
                />
              )}
            />
          </GridCompact4>
          <GridCompact4>
            <Controller
              name="hours"
              control={control}
              render={({ field, fieldState }) => (
                <NumericFormat
                  {...field}
                  customInput={TextField}
                  type="text"
                  onValueChange={({ value: v }) =>
                    field.onChange({ target: { value: v } })
                  }
                  label={t('hours')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  decimalSeparator=","
                  valueIsNumericString
                  required
                />
              )}
            />
          </GridCompact4>
          <GridCompact4>
            <Controller
              name="unit"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  fullWidth
                  onSelected={field.onChange}
                  label={t('unit')}
                  options={unitOptions}
                  error={fieldState.error}
                  valueProp={field.value}
                  required
                />
              )}
            />
          </GridCompact4>
        </GridContainerFlex>
        <GridContainerFlex>
          <GridCompact4>
            <Controller
              name="startDate"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  date={field.value}
                  label={t('startDate')}
                  error={fieldState.error}
                  required
                />
              )}
            />
          </GridCompact4>
          <GridCompact4>
            <Controller
              name="endDate"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  date={field.value}
                  label={t('endDate')}
                  error={fieldState.error}
                  required
                />
              )}
            />
          </GridCompact4>
        </GridContainerFlex>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {t('save')}
          </Button>
        </DialogActions>
        <LoadingOverlay loading={isLoading} />
      </form>
    </StyledDialogMedium>
  );
};

export default AddEditAdditionalCapacityDialog;
